import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StateManagementService, RepresentativeDetails } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { AuthService } from '../../services/auth.service';
import { US_STATES } from '../business-details/business-details.component';
import { AddressAutocompleteComponent, OnboardAddress } from '../../shared/address-autocomplete/address-autocomplete.component';

export const REP_TITLES = [
  { value: 'executive_director', label: 'Executive Director' },
  { value: 'president', label: 'President' },
  { value: 'ceo', label: 'CEO' },
  { value: 'cfo', label: 'CFO' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'director', label: 'Director' },
  { value: 'owner', label: 'Owner' },
  { value: 'other', label: 'Other' },
];

@Component({
  selector: 'app-representative-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
    AddressAutocompleteComponent,
  ],
  templateUrl: './representative-details.component.html',
  styleUrl: './representative-details.component.scss',
})
export class RepresentativeDetailsComponent implements OnInit, OnDestroy {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<RepresentativeDetails>();

  form!: FormGroup;
  loading = false;
  titles = REP_TITLES;
  usStates = US_STATES;
  currentYear = new Date().getFullYear();
  months = [
    { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
    { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
    { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
    { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
  ];
  days = Array.from({ length: 31 }, (_, i) => i + 1);
  years = Array.from({ length: 100 }, (_, i) => this.currentYear - 18 - i);

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: OnboardingApiService,
    private stateService: StateManagementService,
    private snackBar: MatSnackBar,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    const currentUserEmail = this.authService.getCurrentUserEmail() || '';

    // Initialize empty form first
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: currentUserEmail, disabled: true }],
      phone: ['', Validators.required],
      title: ['', Validators.required],
      dobDay: [null, Validators.required],
      dobMonth: [null, Validators.required],
      dobYear: [null, Validators.required],
      addressLookup: [null, null],
      addrLine1: ['', Validators.required],
      addrCity: ['', Validators.required],
      addrState: ['', Validators.required],
      addrZip: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      ssnLast4: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    });

    // Subscribe to state updates and populate form when data arrives
    this.stateService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.representativeDetails) {
        const d = state.representativeDetails;
        this.form.patchValue({
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          email: d.email || currentUserEmail,
          phone: d.phone || '',
          title: d.title || '',
          dobDay: d.dobDay || null,
          dobMonth: d.dobMonth || null,
          dobYear: d.dobYear || null,
          addressLookup: this.buildAddressObject(d.address),
          addrLine1: d.address?.line1 || '',
          addrCity: d.address?.city || '',
          addrState: d.address?.state || '',
          addrZip: d.address?.postal_code || '',
          ssnLast4: d.ssnLast4 || '',
        }, { emitEvent: false });
      } else if (state.partnerPrefill) {
        // Pre-fill contact name and phone from partner referral
        const pf = state.partnerPrefill;
        const patch: Record<string, string> = {};
        if (pf.contactName) {
          const parts = pf.contactName.trim().split(/\s+/);
          if (parts.length >= 2 && !this.form.get('firstName')!.value) {
            patch['firstName'] = parts[0];
            patch['lastName'] = parts.slice(1).join(' ');
          } else if (parts.length === 1 && !this.form.get('firstName')!.value) {
            patch['firstName'] = parts[0];
          }
        }
        if (pf.phone && !this.form.get('phone')!.value) patch['phone'] = pf.phone;
        if (Object.keys(patch).length) {
          this.form.patchValue(patch, { emitEvent: false });
        }
      }
    });

    // Listen to address lookup changes and auto-populate address fields
    this.form.get('addressLookup')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(addr => {
        if (addr) {
          this.form.patchValue({
            addrLine1: addr.line1 || '',
            addrCity: addr.city || '',
            addrState: addr.state || '',
            addrZip: addr.postal_code || '',
          }, { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildAddressObject(addr: any): OnboardAddress | null {
    if (!addr) return null;
    // Return address object if any field has data
    if (addr.line1 || addr.city || addr.state || addr.postal_code) {
      return {
        line1: addr.line1 || '',
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.postal_code || '',
        country: addr.country || 'US',
      };
    }
    return null;
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const v = this.form.getRawValue(); // Use getRawValue to include disabled fields

    const details: RepresentativeDetails = {
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phone: v.phone,
      title: v.title,
      dobDay: v.dobDay,
      dobMonth: v.dobMonth,
      dobYear: v.dobYear,
      address: {
        line1: v.addrLine1,
        city: v.addrCity,
        state: v.addrState,
        postal_code: v.addrZip,
      },
      ssnLast4: v.ssnLast4,
    };

    try {
      await this.api.saveRepresentativeDetails(this.organizationId, details).toPromise();
      this.stateService.setRepresentativeDetails(details);
      this.snackBar.open('Representative details saved!', 'Close', { duration: 3000 });
      this.stepComplete.emit(details);
    } catch (err: any) {
      this.snackBar.open('Error saving details: ' + (err.message || err), 'Close', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }
}
